import { useAtomValue, useSetAtom, type Atom, type PrimitiveAtom, type WritableAtom } from "jotai";
import type React from "react";
import { forwardRef, useCallback, type ComponentProps } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import type {
    WorkflowEditFormStepData,
    WorkflowEditFormStepType,
} from "@/components/automations/automation-edit-form-data";
import { IconConsoleSimple } from "central-icons/IconConsoleSimple";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { ShellInput } from "@/components/flexkit/ShellInput";
import { useEffect, useRef, useState, type FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconPullRequest } from "central-icons/IconPullRequest";
import IconTable from "central-icons/IconTable";
import type { SidePanelEditingState } from "@/routes/automations/automation-edit-page/types";
import { AutomationPullRequestStepEditor } from "@/routes/automations/automation-edit-page/AutomationPullRequestStepEditor";
import { AutomationReportStepEditor } from "@/routes/automations/automation-edit-page/AutomationReportStepEditor";
import { cn } from "@/components/podkit/lib/cn";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import IconTrashCanBase from "central-icons/IconTrashCan";
import { withGitpodSize } from "@/assets/icons/utils";
import { Tooltip } from "@/components/Tooltip";

const IconCrossSmallWithSize = withGitpodSize(IconCrossSmall);
const IconTrashCan = withGitpodSize(IconTrashCanBase);

type AutomationStepEditAreaProps = ComponentProps<"div"> & {
    sidePanelEditingAtom: PrimitiveAtom<SidePanelEditingState>;
    currentEditingStepAtom: Atom<WorkflowEditFormStepData | null>;
    isCreatingStepAtom: Atom<boolean>;
    removeStepAtom: WritableAtom<null, [number], void>;
    updateCurrentStepAtom: WritableAtom<null, [Partial<WorkflowEditFormStepData>], void>;
    addNewStepAtom: WritableAtom<null, [], void>;
    onClose: () => void;
    onStepAdded?: () => void;
};

export const AutomationStepEditArea = forwardRef<HTMLDivElement, AutomationStepEditAreaProps>(
    (
        {
            className,
            sidePanelEditingAtom,
            currentEditingStepAtom,
            isCreatingStepAtom,
            removeStepAtom,
            updateCurrentStepAtom,
            addNewStepAtom,
            onClose,
            onStepAdded,
            ...props
        },
        ref,
    ) => {
        const currentStep = useAtomValue(currentEditingStepAtom);
        const isCreatingStep = useAtomValue(isCreatingStepAtom);
        const sidePanelEditing = useAtomValue(sidePanelEditingAtom);
        const removeStep = useSetAtom(removeStepAtom);
        const isVisible = currentStep !== null;

        const handleRemoveStep = useCallback(() => {
            if (sidePanelEditing?.type === "step") {
                removeStep(sidePanelEditing.index);
                onClose();
            }
        }, [sidePanelEditing, removeStep, onClose]);

        return (
            <div ref={ref} className={cn("relative flex flex-1 grow flex-col overflow-hidden", className)} {...props}>
                {isVisible ? (
                    <>
                        <div className="flex shrink-0 items-center justify-between px-6 pb-3 pt-6">
                            <Text className="text-lg font-medium">
                                {isCreatingStep ? "Add step" : "Edit step"}
                            </Text>
                            {!isCreatingStep && (
                                <div className="flex items-center gap-1">
                                    <Tooltip content="Delete this step" side="bottom" usePortal>
                                        <Button
                                            variant="ghost"
                                            aria-label="Delete step"
                                            onClick={handleRemoveStep}
                                            data-tracking-id="delete-step-edit-panel"
                                            LeadingIcon={IconTrashCan}
                                            className="text-content-negative hover:text-content-negative"
                                        />
                                    </Tooltip>
                                    <Button
                                        variant="ghost"
                                        aria-label="Close edit panel"
                                        onClick={onClose}
                                        data-tracking-id="close-step-edit-panel"
                                        LeadingIcon={IconCrossSmallWithSize}
                                    />
                                </div>
                            )}
                        </div>
                        {currentStep && (
                            <AutomationStepEditor
                                updateCurrentStepAtom={updateCurrentStepAtom}
                                addNewStepAtom={addNewStepAtom}
                                isCreating={isCreatingStep}
                                step={currentStep}
                                onClose={onClose}
                                onStepAdded={onStepAdded}
                            />
                        )}
                    </>
                ) : null}
            </div>
        );
    },
);
AutomationStepEditArea.displayName = "AutomationStepEditArea";

type AutomationStepEditorProps = {
    updateCurrentStepAtom: WritableAtom<null, [Partial<WorkflowEditFormStepData>], void>;
    addNewStepAtom: WritableAtom<null, [], void>;
    isCreating: boolean;
    step: WorkflowEditFormStepData;
    onClose: () => void;
    onStepAdded?: () => void;
};

const AutomationStepEditor: FC<AutomationStepEditorProps> = ({
    updateCurrentStepAtom,
    addNewStepAtom,
    isCreating,
    step,
    onClose,
    onStepAdded,
}) => {
    const updateCurrentStep = useSetAtom(updateCurrentStepAtom);
    const addNewStep = useSetAtom(addNewStepAtom);

    const handleStepChange = useCallback(
        (updates: Partial<WorkflowEditFormStepData>) => {
            updateCurrentStep(updates);
        },
        [updateCurrentStep],
    );

    const handleTypeChange = useCallback(
        (newType: WorkflowEditFormStepType) => {
            const currentType = step.type;
            const canPreserveContent =
                (currentType === "prompt" || currentType === "command") &&
                (newType === "prompt" || newType === "command");

            let newStep: WorkflowEditFormStepData;
            if (canPreserveContent && (step.type === "prompt" || step.type === "command")) {
                newStep = { ...step, type: newType } as WorkflowEditFormStepData;
            } else {
                switch (newType) {
                    case "command":
                    case "prompt":
                        newStep = { id: step.id, type: newType, content: "" };
                        break;
                    case "pr":
                        newStep = {
                            id: step.id,
                            type: newType,
                            title: "",
                            description: "",
                            branch: "",
                            isDraft: false,
                        };
                        break;
                    case "report":
                        newStep = { id: step.id, type: newType, outputs: [] };
                        break;
                }
            }

            // Type changes should be immediate, not debounced
            updateCurrentStep(newStep);
        },
        [step, updateCurrentStep],
    );

    const handleAdd = () => {
        addNewStep();
        onStepAdded?.();
    };

    const handleCancel = () => {
        onClose();
    };

    let contentEditor: React.ReactNode | null;
    switch (step.type) {
        case "command":
            contentEditor = (
                <StepCommandEditor content={step.content} onChange={(content) => handleStepChange({ content })} />
            );
            break;
        case "prompt":
            contentEditor = (
                <StepPromptEditor content={step.content} onChange={(content) => handleStepChange({ content })} />
            );
            break;
        case "pr":
            contentEditor = <AutomationPullRequestStepEditor step={step} onChange={handleStepChange} />;
            break;
        case "report":
            contentEditor = <AutomationReportStepEditor step={step} onChange={handleStepChange} />;
            break;
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col bg-surface-primary">
            <div className={cn("flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto", !isCreating && "pb-6")}>
                <StepTypeSelect type={step.type} onTypeChange={handleTypeChange} />
                <div className="flex-1">{contentEditor}</div>
            </div>
            {isCreating && (
                <div className="flex shrink-0 justify-start gap-2 border-t border-border-subtle py-4 pl-6 pr-4">
                    <Button variant="primary" onClick={handleAdd} data-tracking-id="add">
                        Add
                    </Button>
                    <Button variant="outline" onClick={handleCancel} data-tracking-id="cancel">
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
};

type StepTypeSelectProps = {
    type: WorkflowEditFormStepType;
    onTypeChange: (type: WorkflowEditFormStepType) => void;
};

const StepTypeSelect: FC<StepTypeSelectProps> = ({ type, onTypeChange }) => {
    let icon: React.ReactNode | null = null;
    let label: string | null = null;

    switch (type) {
        case "command":
            icon = <IconConsoleSimple size={16} />;
            label = "Shell script";
            break;
        case "prompt":
            icon = <IconBubbleText6 size={16} />;
            label = "Prompt";
            break;
        case "pr":
            icon = <IconPullRequest size={16} />;
            label = "Pull request";
            break;
        case "report":
            icon = <IconTable size={16} />;
            label = "Report";
            break;
    }

    return (
        <Select value={type} onValueChange={(value) => onTypeChange(value as WorkflowEditFormStepType)}>
            <SelectTrigger loading={false} className="mx-6 w-fit">
                <SelectValue placeholder="Select step type">
                    <div className="flex items-center gap-2">
                        {icon}
                        <span>{label}</span>
                    </div>
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="command">
                    <div className="flex items-center gap-2 text-base">
                        <IconConsoleSimple size={16} />
                        <span>Shell script</span>
                    </div>
                </SelectItem>
                <SelectItem value="prompt">
                    <div className="flex items-center gap-2 text-base">
                        <IconBubbleText6 size={16} />
                        <span>Prompt</span>
                    </div>
                </SelectItem>
                <SelectItem value="pr">
                    <div className="flex items-center gap-2 text-base">
                        <IconPullRequest size={16} />
                        <span>Pull request</span>
                    </div>
                </SelectItem>
                <SelectItem value="report">
                    <div className="flex items-center gap-2 text-base">
                        <IconTable size={16} />
                        <span>Report</span>
                    </div>
                </SelectItem>
            </SelectContent>
        </Select>
    );
};

const StepPromptEditor: FC<{ content: string; onChange: (content: string) => void }> = ({ content, onChange }) => {
    return (
        <div className="mx-6 h-full max-h-full rounded-xl bg-surface-muted p-1.5 transition-colors focus-within:bg-ring-default">
            <Textarea
                dontAutosize
                className="h-full min-h-40 resize-none rounded-lg border border-border-base bg-surface-primary p-3 text-base shadow-sm outline-none focus-within:ring-0 focus:border-border-strong focus:ring-0 focus-visible:ring-0"
                value={content}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter the prompt for this step"
            />
        </div>
    );
};

const StepCommandEditor: FC<{ content: string; onChange: (content: string) => void }> = ({ content, onChange }) => {
    const [availableHeight, setAvailableHeight] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current) {
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setAvailableHeight(entry.contentRect.height);
                }
            });
            observer.observe(containerRef.current);
            return () => observer.disconnect();
        }
    }, [containerRef]);

    return (
        <div ref={containerRef} className="h-full max-h-full shrink px-6">
            {availableHeight && (
                <ShellInput
                    className="h-full max-h-none"
                    value={content}
                    onChange={onChange}
                    placeholder="Enter shell commands"
                    minHeight={120}
                    maxHeight={availableHeight}
                />
            )}
        </div>
    );
};
